import { state } from './state.js';
import { STYLES_DATA } from './styles-data.js';
import { applyStyle, updateZSpacing, applyPavement, updatePavementRepeat } from './scene-setup.js';
import {
  setCameraType,
  setCameraMode,
  setCameraZoom,
  setCameraHeight,
  setCameraPanX,
  setFloorHeight,
  getActiveCamera
} from './camera-controls.js';
import { spawnAvatar } from './avatar-controller.js';
import {
  fetchProjects,
  closeExplorerModal,
  getProjectApiHeaders,
  setStoredOwnerKey,
  updateRecoveryUi
} from './project-explorer.js';

let toastTimer = null;

/**
 * Hiển thị Toast thông báo
 */
export function showToast(msg, type = 'info') {
  const toast = document.getElementById('app-toast');
  const toastMsg = document.getElementById('app-toast-msg');
  const toastIcon = document.getElementById('app-toast-icon');
  if (!toast || !toastMsg) return;

  toastMsg.textContent = msg;
  toast.className = `app-toast active ${type}`;
  if (toastIcon) {
    toastIcon.textContent = type === 'success' ? '✨' : type === 'error' ? '❌' : 'ℹ️';
  }

  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toast.classList.remove('active');
  }, 3500);
}

export { escapeHtml } from '../core/html.js';

export function formatFileSize(bytes) {
  if (!bytes || isNaN(bytes)) return '0 KB';
  if (bytes < 1024) return bytes + ' B';
  return (bytes / 1024).toFixed(1) + ' KB';
}

export function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Chụp Thumbnail từ Canvas
 */
export function captureSceneThumbnail() {
  const activeCam = getActiveCamera();
  if (state.renderer && state.scene) {
    state.renderer.render(state.scene, activeCam);
    return state.renderer.domElement.toDataURL('image/jpeg', 0.85);
  }
  return null;
}

export function updateSaveSummaryChips() {
  const styleChip = document.getElementById('save-summary-style');
  const camChip = document.getElementById('save-summary-camera');
  const avatarChip = document.getElementById('save-summary-avatar');
  const zChip = document.getElementById('save-summary-z');
  const paveChip = document.getElementById('save-summary-pave');

  const styleObj = STYLES_DATA[state.currentStyleKey] || STYLES_DATA.ghibli;
  const floorSign = state.currentFloorHeight >= 0 ? '+' : '';
  if (styleChip) styleChip.textContent = `${styleObj.icon} ${styleObj.name}`;
  if (camChip)
    camChip.textContent = `${state.currentCameraType === 'orthographic' ? '📐 Ortho' : '🎥 Persp'} • Zoom ${state.manualCamZoom.toFixed(2)}x`;
  if (avatarChip) avatarChip.textContent = `👤 ${state.currentAvatarId} (${state.currentAnimName})`;
  if (zChip) zChip.textContent = `Z: ${state.zSpacingFactor.toFixed(2)}x`;
  if (paveChip)
    paveChip.textContent = `🛣️ ${state.currentPavement} (${floorSign}${state.currentFloorHeight.toFixed(2)}m)`;
}

export function openSaveModal() {
  state.currentCapturedThumb = captureSceneThumbnail();
  const thumbImg = document.getElementById('save-snapshot-img');
  if (thumbImg) thumbImg.src = state.currentCapturedThumb;

  state.isUserEditingFilename = false;
  const nameInput = document.getElementById('input-save-name');
  const fileInput = document.getElementById('input-save-filename');
  const descInput = document.getElementById('input-save-desc');

  const styleObj = STYLES_DATA[state.currentStyleKey] || STYLES_DATA.ghibli;
  const timeStr = new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });

  let defaultName = `Bối Cảnh ${styleObj.name} (${timeStr})`;
  let defaultFile = `${state.currentStyleKey}-${Date.now().toString().slice(-6)}`;
  let defaultDesc = `Bối cảnh phong cách ${styleObj.name}, camera ${state.currentCameraType === 'orthographic' ? 'Orthographic' : 'Perspective'}, sàn ${state.currentPavement}, nhân vật ${state.currentAvatarId}.`;

  if (state.activeLoadedProject) {
    defaultName = `${state.activeLoadedProject.name} (Bản Mới)`;
    defaultFile = `${slugify(state.activeLoadedProject.name)}-moi`;
    defaultDesc = state.activeLoadedProject.description || defaultDesc;
  }

  if (nameInput) nameInput.value = defaultName;
  if (fileInput) fileInput.value = defaultFile;
  if (descInput) descInput.value = defaultDesc;

  updateSaveSummaryChips();

  const modal = document.getElementById('modal-save-scene');
  if (modal) modal.classList.add('active');
  if (nameInput) nameInput.focus();
}

export function closeSaveModal() {
  const modal = document.getElementById('modal-save-scene');
  if (modal) modal.classList.remove('active');
}

export async function confirmSaveCurrentScene() {
  const nameInput = document.getElementById('input-save-name');
  const fileInput = document.getElementById('input-save-filename');
  const descInput = document.getElementById('input-save-desc');

  const name = nameInput ? nameInput.value.trim() : '';
  if (!name) {
    showToast('⚠️ Vui lòng nhập tên dự án!', 'error');
    if (nameInput) nameInput.focus();
    return;
  }

  let filename = fileInput ? fileInput.value.trim() : '';
  if (!filename) {
    filename = slugify(name);
  }
  filename = slugify(filename);
  if (!filename.endsWith('.json')) {
    filename = `${filename}.json`;
  }

  const desc = descInput ? descInput.value.trim() : '';

  if (!state.currentCapturedThumb) {
    state.currentCapturedThumb = captureSceneThumbnail();
  }

  const payload = {
    filename,
    name,
    description: desc,
    camera: {
      type: state.currentCameraType,
      mode: state.currentCameraMode,
      zSpacing: state.zSpacingFactor,
      autoSway: state.isAutoSway,
      zoom: state.manualCamZoom,
      height: state.manualCamHeight,
      panX: state.manualCamPanX
    },
    environment: {
      style: state.currentStyleKey,
      pavement: state.currentPavement,
      pavementRepeat: state.pavementRepeat,
      floorHeight: state.currentFloorHeight
    },
    avatar: {
      preset: state.currentAvatarId,
      anim: state.currentAnimName
    },
    layers: {
      fg: state.fgMesh ? state.fgMesh.visible : true,
      avatar: state.currentAvatar ? state.currentAvatar.root.visible : true,
      props: state.propsGroup ? state.propsGroup.visible : true,
      ground: state.groundGroup ? state.groundGroup.visible : true,
      contactShadow: state.contactShadowGroup ? state.contactShadowGroup.visible : true,
      modular: state.modularTownGroup ? state.modularTownGroup.visible : true,
      bg: state.bgMesh ? state.bgMesh.visible : true
    },
    thumbnail: state.currentCapturedThumb
  };

  try {
    const res = await fetch('/api/projects', {
      method: 'POST',
      headers: getProjectApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload)
    });

    if (!res.ok) throw new Error('Máy chủ từ chối ghi file');
    const data = await res.json().catch(() => ({}));
    if (data.ownerKey) {
      setStoredOwnerKey(data.ownerKey);
      updateRecoveryUi();
    }
    state.activeLoadedProject = payload;
    showToast(`💾 Đã lưu thành công dự án: ${name}!`, 'success');
    closeSaveModal();
    await fetchProjects(data.filename || filename);
  } catch (err) {
    console.error('[confirmSaveCurrentScene Error]:', err);
    showToast(`❌ Lỗi khi lưu dự án: ${err.message}`, 'error');
  }
}

/**
 * Nạp kịch bản vào Scene
 */
export async function loadProjectIntoScene(data) {
  if (!data) return;
  showToast(`⏳ Đang nạp bối cảnh: ${data.name || data.id || 'Dự án'}...`, 'info');

  try {
    const targetStyle = data.environment?.style || data.style || 'ghibli';
    await applyStyle(targetStyle);

    const targetCamType = data.camera?.type || data.cameraType || 'orthographic';
    setCameraType(targetCamType);

    const targetCamMode = data.camera?.mode || 'parallax';
    setCameraMode(targetCamMode);

    state.isAutoSway = data.camera?.autoSway !== false;
    const toggleAutoSwayEl = document.getElementById('toggle-auto-sway');
    if (toggleAutoSwayEl) toggleAutoSwayEl.checked = state.isAutoSway;

    const zSpacing = typeof data.camera?.zSpacing === 'number' ? data.camera.zSpacing : 1.0;
    updateZSpacing(zSpacing);
    const sliderZEl = document.getElementById('slider-z-spacing');
    if (sliderZEl) sliderZEl.value = zSpacing;

    const targetPave = data.environment?.pavement || 'terrazzo';
    await applyPavement(targetPave);

    const targetPaveRepeat = data.environment?.pavementRepeat || 12;
    updatePavementRepeat(targetPaveRepeat);
    const sliderRepeatEl = document.getElementById('slider-pave-repeat');
    if (sliderRepeatEl) sliderRepeatEl.value = targetPaveRepeat;

    if (data.environment && data.environment.floorHeight !== undefined) {
      setFloorHeight(data.environment.floorHeight);
    } else {
      setFloorHeight(0.0);
    }

    if (data.camera) {
      if (data.camera.zoom !== undefined) setCameraZoom(data.camera.zoom);
      else setCameraZoom(1.0);

      if (data.camera.height !== undefined) setCameraHeight(data.camera.height);
      else setCameraHeight(2.8);

      if (data.camera.panX !== undefined) setCameraPanX(data.camera.panX);
      else setCameraPanX(0.0);
    }

    const presetId = data.avatar?.preset || data.avatarPreset || 'CO_GAI_AO_DAI';
    const animName = data.avatar?.anim || 'walk';
    state.currentAvatarId = presetId;
    state.currentAnimName = animName;
    const selectAvatarEl = document.getElementById('select-avatar');
    if (selectAvatarEl) selectAvatarEl.value = presetId;
    const selectAnimEl = document.getElementById('select-anim');
    if (selectAnimEl) selectAnimEl.value = animName;
    spawnAvatar(presetId, animName);

    if (data.layers) {
      if (data.layers.fg !== undefined) {
        if (state.fgMesh) state.fgMesh.visible = !!data.layers.fg;
        if (state.lanternPointLight) state.lanternPointLight.visible = !!data.layers.fg;
        const el = document.getElementById('layer-fg');
        if (el) el.checked = !!data.layers.fg;
      }
      if (data.layers.avatar !== undefined) {
        if (state.currentAvatar) state.currentAvatar.root.visible = !!data.layers.avatar;
        const el = document.getElementById('layer-avatar');
        if (el) el.checked = !!data.layers.avatar;
      }
      if (data.layers.props !== undefined) {
        if (state.propsGroup) state.propsGroup.visible = !!data.layers.props;
        const el = document.getElementById('layer-props');
        if (el) el.checked = !!data.layers.props;
      }
      if (data.layers.ground !== undefined) {
        if (state.groundGroup) state.groundGroup.visible = !!data.layers.ground;
        const el = document.getElementById('layer-ground');
        if (el) el.checked = !!data.layers.ground;
      }
      if (data.layers.contactShadow !== undefined) {
        if (state.contactShadowGroup) state.contactShadowGroup.visible = !!data.layers.contactShadow;
        const el = document.getElementById('layer-contact-shadow');
        if (el) el.checked = !!data.layers.contactShadow;
      }
      if (data.layers.modular !== undefined) {
        if (state.modularTownGroup) state.modularTownGroup.visible = !!data.layers.modular;
        const el = document.getElementById('layer-modular');
        if (el) el.checked = !!data.layers.modular;
      }
      if (data.layers.bg !== undefined) {
        if (state.bgMesh) state.bgMesh.visible = !!data.layers.bg;
        const el = document.getElementById('layer-bg');
        if (el) el.checked = !!data.layers.bg;
      }
    }

    const activeCam = getActiveCamera();
    state.mouse.targetX = 0;
    state.mouse.targetY = 0;
    state.mouse.x = 0;
    state.mouse.y = 0;
    if (state.currentCameraMode === 'orbit' && state.controls) {
      state.controls.target.set(0, 4.2 + state.currentFloorHeight * 0.5, 0);
      activeCam.position.set(0, 4.6 + state.currentFloorHeight * 0.5, 16.0);
    } else {
      activeCam.position.set(state.manualCamPanX, state.manualCamHeight, 16.0);
      activeCam.lookAt(state.manualCamPanX * 0.28, state.manualCamHeight - 1.1 + state.currentFloorHeight * 0.5, 0);
    }

    state.activeLoadedProject = data;
    showToast(`✨ Đã nạp thành công bối cảnh: ${data.name || data.filename}!`, 'success');
  } catch (err) {
    console.error('[loadProjectIntoScene Error]:', err);
    showToast(`❌ Lỗi khi nạp dự án: ${err.message}`, 'error');
  }
}

/**
 * Nhân bản dự án
 */
export async function duplicateProject(project) {
  if (!project) return;
  try {
    const copyName = `${project.name || 'Dự Án'} (Bản Sao)`;
    const rawSlug = slugify(copyName);
    const copyFilename = `${rawSlug}-${Date.now().toString().slice(-4)}.json`;

    const payload = {
      ...project,
      name: copyName,
      filename: copyFilename
    };

    const res = await fetch('/api/projects', {
      method: 'POST',
      headers: getProjectApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload)
    });

    if (!res.ok) throw new Error('Không thể tạo bản sao');
    const data = await res.json().catch(() => ({}));
    if (data.ownerKey) {
      setStoredOwnerKey(data.ownerKey);
      updateRecoveryUi();
    }
    showToast(`📋 Đã nhân bản thành công: ${copyName}!`, 'success');
    await fetchProjects(data.filename || copyFilename);
  } catch (err) {
    console.error('[duplicateProject Error]:', err);
    showToast(`❌ Lỗi nhân bản: ${err.message}`, 'error');
  }
}

/**
 * Đặt lại trạng thái bối cảnh mặc định
 */
export function resetToDefaultScene() {
  state.activeLoadedProject = null;
  applyStyle('ghibli');
  setCameraType('orthographic');
  setCameraMode('parallax');
  state.isAutoSway = true;

  const toggleAutoSwayEl = document.getElementById('toggle-auto-sway');
  if (toggleAutoSwayEl) toggleAutoSwayEl.checked = true;

  updateZSpacing(1.0);
  const sliderZEl = document.getElementById('slider-z-spacing');
  if (sliderZEl) sliderZEl.value = 1.0;

  applyPavement('terrazzo');
  updatePavementRepeat(12);
  const sliderRepeatEl = document.getElementById('slider-pave-repeat');
  if (sliderRepeatEl) sliderRepeatEl.value = 12;

  setFloorHeight(0.0);
  setCameraZoom(1.0);
  setCameraHeight(2.8);
  setCameraPanX(0.0);

  spawnAvatar('CO_GAI_AO_DAI', 'walk');
  const selAvatar = document.getElementById('select-avatar');
  if (selAvatar) selAvatar.value = 'CO_GAI_AO_DAI';
  const selAnim = document.getElementById('select-anim');
  if (selAnim) selAnim.value = 'walk';

  if (state.fgMesh) state.fgMesh.visible = true;
  if (state.lanternPointLight) state.lanternPointLight.visible = true;
  const lFg = document.getElementById('layer-fg');
  if (lFg) lFg.checked = true;

  if (state.currentAvatar) state.currentAvatar.root.visible = true;
  const lAv = document.getElementById('layer-avatar');
  if (lAv) lAv.checked = true;

  if (state.propsGroup) state.propsGroup.visible = true;
  const lPr = document.getElementById('layer-props');
  if (lPr) lPr.checked = true;

  if (state.groundGroup) state.groundGroup.visible = true;
  const lGr = document.getElementById('layer-ground');
  if (lGr) lGr.checked = true;

  if (state.contactShadowGroup) state.contactShadowGroup.visible = true;
  const lCs = document.getElementById('layer-contact-shadow');
  if (lCs) lCs.checked = true;

  if (state.modularTownGroup) state.modularTownGroup.visible = true;
  const lMd = document.getElementById('layer-modular');
  if (lMd) lMd.checked = true;

  if (state.bgMesh) state.bgMesh.visible = true;
  const lBg = document.getElementById('layer-bg');
  if (lBg) lBg.checked = true;

  const activeCam = getActiveCamera();
  state.mouse.targetX = 0;
  state.mouse.targetY = 0;
  state.mouse.x = 0;
  state.mouse.y = 0;
  activeCam.position.set(0, 2.8, 16.0);
  activeCam.lookAt(0, 1.7, 0);

  closeExplorerModal();
  showToast('✨ Đã tạo không gian dự án mới! Bấm "Lưu" khi bạn sẵn sàng.', 'success');
}
